// Px3 Pipeline - intake sources (the portals allowed to POST orders).
//
// A source is a name and an API key. The row stores a sha256 of the key plus its
// first 8 characters; the cleartext exists exactly once, in the response to the
// create call, and this screen is the only place it is ever shown. If the
// operator closes that modal without copying it, the fix is a new source - not a
// lookup, because there is nothing to look up.
//
// Revoking is not deleting: the row stays so the orders it submitted keep their
// provenance, and a revoked key is refused in SQL rather than in application
// code.

import { useCallback, useEffect, useState } from 'react';

import type { Px3ShowToast } from '../Px3';
import { px3Api, px3ErrorMessage, type Px3Source } from '../px3Api';

function fmtDate(iso: string | null): string {
  return iso
    ? new Date(iso).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '-';
}

export function SourcesView({ showToast }: { showToast: Px3ShowToast }): JSX.Element {
  const [sources, setSources] = useState<Px3Source[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState('');
  const [creating, setCreating] = useState(false);

  /** The one-time key, held in component state only for as long as its modal is
   *  open. Nothing persists it and no later fetch can bring it back. */
  const [newKey, setNewKey] = useState<{ name: string; key: string } | null>(null);

  const [revokeTarget, setRevokeTarget] = useState<Px3Source | null>(null);
  const [revoking, setRevoking] = useState(false);

  const refresh = useCallback(async (): Promise<void> => {
    try {
      setSources(await px3Api.listSources());
      setError(null);
    } catch (err) {
      setError(px3ErrorMessage(err));
    } finally {
      setLoaded(true);
    }
  }, []);

  // No polling: a source list changes only when someone on this screen changes
  // it, and `last_used_at` moving a minute late costs nothing.
  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function createSource(): Promise<void> {
    const trimmed = name.trim();
    if (trimmed === '') return;
    setCreating(true);
    try {
      const { source, api_key } = await px3Api.createSource(trimmed);
      setName('');
      setShowCreate(false);
      setNewKey({ name: source.name, key: api_key });
      await refresh();
    } catch (err) {
      showToast('error', px3ErrorMessage(err));
    } finally {
      setCreating(false);
    }
  }

  async function revoke(source: Px3Source): Promise<void> {
    setRevoking(true);
    try {
      await px3Api.revokeSource(source.id);
      setRevokeTarget(null);
      await refresh();
      showToast('success', `${source.name} revoked. Its key is refused from now on.`);
    } catch (err) {
      showToast('error', px3ErrorMessage(err));
    } finally {
      setRevoking(false);
    }
  }

  async function copyKey(key: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(key);
      showToast('success', 'Key copied. Paste it into the portal now - it is not shown again.');
    } catch {
      // Clipboard access can be refused (an insecure origin, a locked-down
      // browser). Say so rather than pretending it worked - the key is on
      // screen and selectable, so the operator still has a way through.
      showToast('warn', 'The browser refused clipboard access. Select the key and copy it by hand.');
    }
  }

  return (
    <>
      {error && (
        <div className="px3-toast px3-toast--error">Could not load sources: {error}</div>
      )}

      <section className="px3-card">
        <div className="px3-card-header">
          <h2 className="px3-card-title">Sources</h2>
          <div className="px3-actions">
            <button
              type="button"
              className="px3-btn px3-btn--ghost px3-btn--small"
              onClick={() => void refresh()}
            >
              Refresh
            </button>
            <button
              type="button"
              className="px3-btn px3-btn--primary"
              onClick={() => setShowCreate(true)}
            >
              New source
            </button>
          </div>
        </div>
        <div className="px3-table-wrap">
          <table className="px3-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Key prefix</th>
                <th>Created</th>
                <th>By</th>
                <th>Last used</th>
                <th className="px3-col-center">Status</th>
                <th className="px3-col-center" />
              </tr>
            </thead>
            <tbody>
              {sources.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px3-empty">
                    {!loaded
                      ? 'Loading sources...'
                      : error !== null
                        ? 'The source list could not be loaded - this is not an empty list.'
                        : 'No sources yet. A portal needs one before it can POST an order.'}
                  </td>
                </tr>
              ) : (
                sources.map((s) => (
                  <tr key={s.id}>
                    <td>{s.name}</td>
                    <td className="px3-cell-mono">{s.api_key_prefix}...</td>
                    <td className="px3-cell-faint">{fmtDate(s.created_at)}</td>
                    <td className="px3-cell-faint">{s.created_by ?? '-'}</td>
                    <td className="px3-cell-faint">{fmtDate(s.last_used_at)}</td>
                    <td className="px3-col-center">
                      <span className={`px3-badge px3-badge--${s.revoked_at ? 'err' : 'ok'}`}>
                        {s.revoked_at ? 'revoked' : 'active'}
                      </span>
                    </td>
                    <td className="px3-col-center">
                      {s.revoked_at === null && (
                        <button
                          type="button"
                          className="px3-btn px3-btn--ghost px3-btn--small"
                          onClick={() => setRevokeTarget(s)}
                        >
                          Revoke
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      {showCreate && (
        <div className="px3-modal-overlay">
          <div className="px3-modal">
            <div className="px3-modal-header">
              <h3 className="px3-modal-title">New source</h3>
            </div>
            <div className="px3-modal-body">
              <div className="px3-field">
                <label className="px3-field-label" htmlFor="px3-source-name">
                  Name
                </label>
                <input
                  id="px3-source-name"
                  className="px3-input"
                  type="text"
                  value={name}
                  maxLength={100}
                  placeholder="coldlabs-portal"
                  onChange={(e) => setName(e.target.value)}
                  onKeyDown={(e) => {
                    // `creating` matters here and not only on the button: the
                    // button is disabled mid-create, but Enter bypasses it, and
                    // two quick presses would mint two keys for one portal.
                    if (e.key === 'Enter' && !creating) void createSource();
                  }}
                  spellCheck={false}
                  autoFocus
                />
                <span className="px3-hint">
                  Who is submitting orders. It labels every order they send and it is what you will
                  revoke by, so name the portal, not the person.
                </span>
              </div>
            </div>
            <div className="px3-modal-footer">
              <button
                type="button"
                className="px3-btn px3-btn--primary"
                disabled={creating || name.trim() === ''}
                onClick={() => void createSource()}
              >
                {creating ? 'Creating...' : 'Create and show the key'}
              </button>
              <button
                type="button"
                className="px3-btn px3-btn--secondary"
                disabled={creating}
                onClick={() => setShowCreate(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {newKey && (
        <div className="px3-modal-overlay">
          <div className="px3-modal">
            <div className="px3-modal-header">
              <h3 className="px3-modal-title">API key for {newKey.name}</h3>
            </div>
            <div className="px3-modal-body">
              <div className="px3-notice px3-notice--warn">
                <strong>This is the only time this key is shown.</strong> The row stores a hash of
                it, so nothing can read it back. If it is lost, create a new source and revoke this
                one.
              </div>
              <div className="px3-keybox">
                <code>{newKey.key}</code>
                <button
                  type="button"
                  className="px3-btn px3-btn--secondary px3-btn--small"
                  onClick={() => void copyKey(newKey.key)}
                >
                  Copy
                </button>
              </div>
              <span className="px3-hint">
                The portal sends it as <code>X-API-Key</code> on every request to
                <code> /api/px3/orders</code>. Never in a URL.
              </span>
            </div>
            <div className="px3-modal-footer">
              <button
                type="button"
                className="px3-btn px3-btn--primary"
                onClick={() => setNewKey(null)}
              >
                I have copied it
              </button>
            </div>
          </div>
        </div>
      )}

      {revokeTarget && (
        <div className="px3-modal-overlay">
          <div className="px3-modal">
            <div className="px3-modal-header">
              <h3 className="px3-modal-title">Revoke this source?</h3>
            </div>
            <div className="px3-modal-body">
              <p>
                Its key stops working immediately and every later request from that portal gets a
                401. Orders it already submitted keep running and stay visible. This cannot be
                undone - a portal that needs access again needs a new source.
              </p>
              <p className="px3-cell-faint">
                {revokeTarget.name} ({revokeTarget.api_key_prefix}...)
              </p>
            </div>
            <div className="px3-modal-footer">
              <button
                type="button"
                className="px3-btn px3-btn--danger"
                disabled={revoking}
                onClick={() => void revoke(revokeTarget)}
              >
                {revoking ? 'Revoking...' : 'Revoke the key'}
              </button>
              <button
                type="button"
                className="px3-btn px3-btn--secondary"
                disabled={revoking}
                onClick={() => setRevokeTarget(null)}
              >
                Keep it active
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
